import type { AuthSessionResponse } from '@/lib/api';

/**
 * What `/` should show. Pure, so the decision table is testable without a router,
 * a query client, or a network — the same shape used for deploy freshness and
 * branch classification elsewhere in this repo.
 *
 * Kept separate from the component precisely because this is the load-bearing
 * part: `/` is the one route that must behave differently for a stranger and an
 * operator, and a mistake here either hides the app from its owner or leaks the
 * console to the public.
 */
export type RootView = 'loading' | 'app' | 'landing';

export function resolveRootView(input: {
  readonly isLoading: boolean;
  readonly data: Pick<AuthSessionResponse, 'authenticated'> | undefined;
}): RootView {
  // Render nothing while the session resolves. Showing the landing page first
  // would flash marketing copy at an operator on every visit.
  if (input.isLoading) return 'loading';

  // Signed in -> the app, which is exactly what `/` did unconditionally before.
  if (input.data?.authenticated === true) return 'app';

  // Everything else -> the public page. That deliberately includes a FAILED
  // query (data undefined): `/` is a public surface, so a stranger gets the
  // product page rather than an internal error, and the operator meets the real
  // failure at /chat where AuthGate reports it properly.
  return 'landing';
}
